import find from 'lodash/find';
import random from 'lodash/random';
import BaseSubscription from './BaseSubscription';
import {
  detectBrowser,
  getFromLocalStorage,
  setToLocalStorage
} from './../utils/utilsGlobal';


export const OPENED_TABS_WATCHER_SYNC_TABS = 'OPENED_TABS_WATCHER_SYNC_TABS';
export const OPENED_TABS_WATCHER_RECEIVED_USER_GESTURE = 'OPENED_TABS_WATCHER_RECEIVED_USER_GESTURE';


export default class OpenedTabsWatcher extends BaseSubscription {

  DEFAULT_CURRENT_TAB_ID = 'default';
  currentTabId = null;

  constructor(){
    super();
    document.body.addEventListener('click', this.onInitialUserGesture);
  }

  // Must be initialized when serviceWorker is registered (on PWA_REGISTERED_SERVICE_WORKER event)
  initialize = () => {
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', this.onServiceWorkerMessage);
      const { controller } = navigator.serviceWorker;
      if (controller) {
        controller.postMessage({ eventType: 'requestOpenedTabs' });
      }
    }
  };

  onInitialUserGesture = (e) => {
    const currentTabs = getFromLocalStorage('OPENED_TABS', []);
    if (!currentTabs || !currentTabs.length) {
      return;
    }

    // In Firefox, click on textarea doesn't unlock autoplay
    const clickOnTextareaInFirefox = detectBrowser() === 'firefox' && e.target.tagName === 'TEXTAREA';

    if (!clickOnTextareaInFirefox) {
      const updatedTabs = currentTabs.map(tab => {
        if (tab.id === this.currentTabId) {
          return {
            ...tab,
            receivedUserGesture: true,
          }
        }
        return tab;
      });
      setToLocalStorage('OPENED_TABS', updatedTabs);
      this.triggerEvent(OPENED_TABS_WATCHER_RECEIVED_USER_GESTURE, null, { firedOnce: true });
      document.body.removeEventListener('click', this.onInitialUserGesture);
    }
  };

  onServiceWorkerMessage = (e) => {
    const { eventType, tabs, currentTabId, syncId } = e?.data || {};
    if (eventType === 'syncOpenedTabs') {
      this.onOpenedTabsChange(tabs, currentTabId, syncId);
    }
  };

  onOpenedTabsChange = (tabs, currentTabId, syncId) => {
    const currentTabs = getFromLocalStorage('OPENED_TABS', []);
    const updatedTabs = tabs.map(({ id, isVisible }) => {
      const receivedUserGesture = find(currentTabs, { id })?.receivedUserGesture || false;
      return { id, isVisible, receivedUserGesture };
    });
    setToLocalStorage('OPENED_TABS', updatedTabs);

    this.currentTabId = currentTabId;
    this.triggerEvent(OPENED_TABS_WATCHER_SYNC_TABS, { tabs: updatedTabs, syncId });
  };

  syncOpenedTabs = () => {
    const { controller } = navigator.serviceWorker || {};
    const syncId = random(1000);
    const maxWaitTime = 750; // if no response from service worker within 750ms, consider that service workers are unregistered/unsupported

    return new Promise(resolve => {
      const onTabSyncSuccess = (data) => {
        if (data.syncId === syncId) {
          this.off(OPENED_TABS_WATCHER_SYNC_TABS, onTabSyncSuccess);
          clearTimeout(waitTimeout);
          resolve(data.tabs);
        }
      };
      const onTabSyncFailure = () => {
        const defaultOpenedTabs = this.generateDefaultOpenedTabs();
        this.onOpenedTabsChange(defaultOpenedTabs, this.DEFAULT_CURRENT_TAB_ID, null);
        resolve(defaultOpenedTabs);
      };
      this.on(OPENED_TABS_WATCHER_SYNC_TABS, onTabSyncSuccess);

      const waitTimeout = setTimeout(onTabSyncFailure, maxWaitTime);
      if (controller) {
        controller.postMessage({ eventType: 'requestOpenedTabs', syncId });
      }
      else {
        onTabSyncFailure();
      }
    });
  };

  generateDefaultOpenedTabs = () => {
    const tab = {
      id: this.DEFAULT_CURRENT_TAB_ID,
      receivedUserGesture: false,
      isVisible: true,
    };
    return [tab];
  };

  runInTab = (tab, callback) => {
    if (tab?.id === this.currentTabId) {
      callback();
    }
  };

  runInTabThatReceivedUserGesture = (callback) => {
    this.syncOpenedTabs().then(tabs => {
      const activeTabThatReceivedUserGesture = find(tabs, { isVisible: true, receivedUserGesture: true });
      const latestTabThatReceivedUserGesture = find(tabs, { receivedUserGesture: true });
      if (activeTabThatReceivedUserGesture || latestTabThatReceivedUserGesture) {
        this.runInTab(activeTabThatReceivedUserGesture || latestTabThatReceivedUserGesture, callback);
      }
      else {
        this.runInActiveTab(callback);
      }
    });
  };

  runInActiveTab = (callback) => {
    return this.syncOpenedTabs().then(tabs => {
      const activeTab = find(tabs, { isVisible: true });
      const latestTab = tabs[0]; // in service worker's clients.matchAll(...) the latest client comes first in the list
      this.runInTab(activeTab || latestTab, callback);
    });
  };
}
